<template>
  <v-app>
    <v-app-bar theme="dark" data-cy="appBar" :height="appBarHeight">
      <FusionAppBar
        :logoSrc="logoSrc"
        :logoWidth="logoWidth"
        :hasDrawer="hasDrawer"
        :isLoading="false"
        :user="user"
        @drawerClicked="drawerToggle"
        @navigateHome="navigateHome()"
      >
        <template v-slot:burger>
          <span :class="{ 'application-burger': hasDrawer && lgAndDown }">
            <v-app-bar-nav-icon
              v-if="hasDrawer && lgAndDown"
              @click.stop="drawerToggle"
              class="mx-2"
              size="x-large"
              density="comfortable"
              :color="theme.current.value.colors.textPrimary"
              data-cy="hamburgerButton"
            />
          </span>
        </template>
        <template v-slot:left-toolbar>
          <div class="application-title">
            <h2>
              Fusion.SaaS
              <em>Base App (V3)</em>
            </h2>
          </div>
        </template>
        <template v-slot:right-toolbar>
          <FusionAuthenticator />
        </template>
      </FusionAppBar>
    </v-app-bar>
    <v-navigation-drawer
      v-if="hasDrawer"
      v-model="drawer.open"
      :permanent="drawerPermanent"
      :order="drawerOrder"
      :rail="drawerMini"
      rail-width="68"
      width="240"
      color="#f2f2f2"
    >
      <FusionSideBar :routes="routes" :drawerMini="drawerMini" />
      <template v-if="drawer.open" v-slot:append>
        <v-list-item @click.stop="openDialog" data-cy="feedbackIcon">
          <template v-slot:prepend>
            <v-list-item-action>
              <v-icon>mdi-bullhorn</v-icon>
            </v-list-item-action>
          </template>
          <v-list-item-title v-if="!drawerMini">
            {{ $t('feedback.feedbackDialog.feedback') }}
          </v-list-item-title>
        </v-list-item>
        <v-divider />
        <span class="footer" v-if="!drawerMini">
          <span>
            &copy; {{ new Date().getFullYear() }}
            {{ $t('app.copyright') }}
          </span>
          <span>v{{ appVersion }}</span>
        </span>
      </template>
    </v-navigation-drawer>
    <FusionFeedbackDialog :open="open" :helpRoute="'/'" @close="closeDialog" />
    <v-main>
      <v-container class="ma-0 pa-0" fluid>
        <router-view class="main-content" />
      </v-container>
      <FusionSnackbar />
    </v-main>
  </v-app>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay, useTheme } from 'vuetify'
import { FusionMixins, FusionStores } from '@fusion/saas-components-v3'

export default defineComponent({
  name: 'ParentComponent',
  setup() {
    const router = useRouter()
    const { drawer, drawerPermanent, drawerMini, drawerToggle } = FusionMixins.drawer()
    const { navigateHome, navigateTo, navigateBack } = FusionMixins.navigation(router)
    const { lgAndDown, mdAndUp } = useDisplay()
    const theme = useTheme()
    const loginas = FusionStores.loginas()

    return {
      router,
      navigateHome,
      navigateTo,
      navigateBack,
      lgAndDown,
      mdAndUp,
      theme,
      drawer,
      drawerPermanent,
      drawerMini,
      drawerToggle,
      loginas
    }
  },
  data() {
    return {
      open: false,
      logoSrc: `./img/tsi_link_logo.svg`
    }
  },
  async mounted() {
    if (this.$auth.isAuthenticated) {
      await this.updatePermissions()
    }
    if (!this.drawerPermanent) {
      this.drawer.open = false
    }
  },
  computed: {
    appVersion() {
      return APP_VERSION
    },
    routes() {
      return this.router.options.routes
    },
    user() {
      let user = {}
      if (this.$auth?.user?.email) {
        user = this.$auth.user
      }
      return user
    },
    hasDrawer() {
      return this.$auth.isAuthenticated
    },
    drawerOrder() {
      return this.drawerPermanent ? 0 : -1
    },
    appBarHeight() {
      return this.mdAndUp ? 64 : 56
    },
    logoWidth() {
      const width = (206 / 64) * this.appBarHeight
      return String(width)
    }
  },
  watch: {
    async '$auth.isAuthenticated'(isAuthenticated: boolean) {
      if (isAuthenticated) {
        await this.updatePermissions()
      }
    }
  },
  methods: {
    openDialog() {
      this.open = true
    },
    closeDialog() {
      this.open = false
    },
    async login() {
      await this.$auth.loginWithRedirect()
    },
    async updatePermissions() {
      if (this.loginas.isLogInAs) {
        await this.loginas.updateImpersonatedUserPermissions(
          this.$components?.subscriptionsAPIHost,
          this.$auth
        )
        this.navigateTo('/')
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.main-content {
  width: 100%;
}

.footer {
  opacity: 0.3;
  padding: 0.4rem;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  max-height: 74px;
  white-space: nowrap;
}

.application-title {
  @media (max-width: 777px) {
    display: none;
  }

  h2 {
    margin: 0;
  }
}

.v-list-item {
  height: 60px;
  :deep(.v-list-item__spacer) {
    padding-left: 24px;
  }

  :deep(.v-list-item__prepend) {
    padding-left: 6px;
    opacity: 0.6;
  }
}

.application-burger {
  background-color: #fff;
  height: 100%;
  flex: inherit;
  display: flex;
  align-items: center;
}

.v-divider--vertical {
  margin-right: 0px !important;
}
</style>
